<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmStopReportingSessionDialog"
            :action-loading="actionLoading.reportingSession"
            @on-confirm="stopReportingSession"
            @on-abort="closeConfirmStopReportingSessionDialog"
        >
            <template #title>
                Zakończenie dziennego raportu
            </template>
            <template #content>
                Czy na pewno chcesz zakończyć pracę?
            </template>
            <template #confirmButton>
                Zakończ
            </template>
        </ConfirmDialog>
        <ReportShiftSummaryDialog
            :is-open="reportShiftSummaryDialog"
            :report-data="currentReportShiftSummary"
            :action-loading="actionLoading.approveReport"
            :stopped-reporting-session="stoppedReportingSession"
            @on-close="closeReportShiftSummaryDialog"
        />

        <CreateProcessReportDialog
            :is-open="createProcessReportDialog"
            :report-processes-data="reportProcesses"
            @on-close="closeCreateProcessReportDialog"
        />

        <CreateAccidentDialog
            :accident="accident"
            :is-open="createAccidentDialog"
            @create-accident="createAccident"
            @close-accident-dialog="closeCreateAccidentDialog"
        />

        <WorkspaceAccidentsListDialog
            :is-open="workspaceAccidentsListDialog"
            :workspace-accidents-list-data="unsolvedAccidents"
            @on-close="closeWorkspaceAccidentsListDialog"
        />

        <WorkspaceNotesListDialog
            :is-open="workspaceNotesListDialog"
            :workspace-notes-list-data="workspaceNotes"
            @on-close="closeWorkspaceNotesListDialog"
            @on-abort="closeWorkspaceNotesListDialog"
        />

        <ReportOrderJobsListDialog
            :is-open="reportOrderJobsListDialog"
            :report-order-jobs-list-data="reportOrderJobs"
            :proc-number="procNumber"
            @on-close="closeReportOrderJobsListDialog"
            @on-abort="closeReportOrderJobsListDialog"
        />

        <DetailedReportsListDialog
            :is-open="detailedReportsListDialog"
            :detailed-reports-list-data="detailedReportsList"
            @on-close="closeDetailedReportsListDialog"
        />

        <CodeReaderDialog
            :is-open="codeReaderDialog"
            :settings="codeReaderSettings"
            @code-stream-start="showCodeReaderDialog"
            @code-stream-stop="hideCodeReaderDialog"
            @code-stream-handle="handleQrJSON"
        />

        <ReportOutOfSequenceCodeDialog
            :is-open="reportOutOfSequenceCodeDialog"
            @on-save="saveReportOutOfSequenceCode"
            @on-close="closeReportOutOfSequenceCodeDialog"
        />

        <ProcessDialog
            :is-open="processDialog"
            :machine-processes="filteredMachineProcesses"
            :default-process="defaultProcess"
            @on-save="saveProcess"
            @on-close="closeProcessDialog"
        />

        <page-title>Raportowanie / Zadania</page-title>

        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col cols="12" md="6" lg="8">
                            <h2 class="text-h6">
                                Witaj {{ userName }}
                            </h2>

                            <p class="mt-2">
                                Dziś jest {{ currentDate }}
                            </p>
                            <p v-if="isReportingSessionActive" class="mt-2">
                                Aktualna zmiana: {{ userShift }}
                            </p>
                        </v-col>
                        <v-col cols="12" md="6" lg="4">
                            <WorkspaceSelector
                                :show-workspace-settings-dialog="workspaceSettingsDialog"
                                :is-loading="actionLoading.workspaces"
                                :can-change-workspace="canChangeWorkspace"
                                :current-workspace="currentWorkspace"
                                :report-settings="reportSettings"
                                @change-workspace="saveWorkspaceSettings"
                            />
                        </v-col>
                    </v-row>
                </v-container>

                <v-divider class="my-4" />

                <v-container v-if="currentMachine && currentMachine.occupancy.isLocked && !currentMachine.occupancy.user.isItMe" class="pa-0">
                    <v-row justify="center">
                        <v-col cols="12" md="8">
                            <v-alert
                                border="left"
                                type="error"
                            >
                                Maszyna jest obecnie zajęta przez użytkownika <strong>{{ currentMachine.occupancy.user.userName }}</strong>.
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-container>
                <v-container v-else-if="isReportUnfinished" class="pa-0">
                    <v-row justify="center">
                        <v-col cols="12" md="8">
                            <v-alert
                                border="left"
                                type="error"
                            >
                                Nie zakończyłeś(-aś) raportu na <strong>{{ unfinishedReport.machineName }}</strong>
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container v-else fluid class="pa-0">
                    <v-row justify="center">
                        <v-col cols="12" md="6">
                            <v-btn
                                v-if="!isReportingSessionActive"
                                color="success"
                                block
                                x-large
                                class="mb-1"
                                data-sentry-label="Rozpoczynanie pracy"
                                :loading="actionLoading.reportingSession"
                                @click="startReportingSession"
                            >
                                Rozpoczynam pracę
                            </v-btn>

                            <v-btn
                                v-if="isReportingSessionActive"
                                color="error"
                                block
                                x-large
                                class="mb-1"
                                data-sentry-label="Kończenie pracy"
                                @click="showConfirmStopReportingSessionDialog"
                            >
                                Kończę na dziś
                            </v-btn>
                        </v-col>
                    </v-row>

                    <template v-if="canReportOnWorkspace && isWorkspaceConfigured">
                        <v-fade-transition>
                            <v-row v-if="isReportingSessionActive">
                                <v-col cols="12">
                                    <ReportingProcessingReportsTable
                                        :headers="reportHeaders"
                                        :items="interruptedDetailedUserReports"
                                        :loading="interruptedDetailedUserReportsLoading"
                                        @update-pagination="updatePagination"
                                        @open-report="openReport"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-card>
                                        <v-container fluid class="py-0">
                                            <v-row align="center" justify="space-between" dense>
                                                <v-col cols="12" md="7" lg="8" xl="9">
                                                    <v-btn
                                                        color="primary"
                                                        outlined
                                                        :loading="actionLoading.createAccident"
                                                        @click="showCreateAccidentDialog"
                                                    >
                                                        Zgłoś awarię
                                                    </v-btn>
                                                    <v-badge
                                                        :content="unsolvedAccidentsAmount"
                                                        :value="unsolvedAccidentsAmount"
                                                        :color="unsolvedAccidentsAmount === '0' ? 'success' : 'error'"
                                                        overlap
                                                    >
                                                        <v-btn
                                                            color="primary"
                                                            outlined
                                                            class="ml-4"
                                                            :loading="actionLoading.workspaceAccidentsList"
                                                            @click="showWorkspaceAccidentsListDialog"
                                                        >
                                                            Awarie w toku
                                                        </v-btn>
                                                    </v-badge>
                                                    <v-btn
                                                        color="primary"
                                                        outlined
                                                        class="ml-4"
                                                        @click="showCreateProcessReportDialog"
                                                    >
                                                        Raportuj czynność
                                                    </v-btn>
                                                    <v-badge
                                                        :content="unreadWorkspaceNotesAmount"
                                                        :value="unreadWorkspaceNotesAmount"
                                                        :color="unreadWorkspaceNotesAmount === '0' ? 'success' : 'warning'"
                                                        overlap
                                                    >
                                                        <v-btn
                                                            color="primary"
                                                            outlined
                                                            class="ml-4"
                                                            :loading="actionLoading.workspaceNotesList"
                                                            @click="showWorkspaceNotesListDialog"
                                                        >
                                                            Notki
                                                        </v-btn>
                                                    </v-badge>
                                                </v-col>
                                                <v-col cols="12" md="5" lg="4" xl="3">
                                                    <v-container fluid class="pa-0">
                                                        <v-row justify="end" dense>
                                                            <v-col v-if="currentWorkspace.typeId == 4" sm="auto" class="d-flex align-center">
                                                                <v-menu
                                                                    offset-y
                                                                    :close-on-content-click="false"
                                                                >
                                                                    <template #activator="{on}">
                                                                        <v-btn
                                                                            class="white--text"
                                                                            :color="'teal'"
                                                                            v-on="on"
                                                                        >
                                                                            Sortownie
                                                                        </v-btn>
                                                                    </template>

                                                                    <v-list>
                                                                        <v-list-item v-for="(item, index) in codes" :key="index">
                                                                            <v-list-item-action>
                                                                                <v-checkbox
                                                                                    :input-value="item.checked"
                                                                                    color="deep-purple accent-4"
                                                                                    @change="updateShippingGroupFilter(item.code)"
                                                                                />
                                                                            </v-list-item-action>
                                                                            <v-list-item-content>
                                                                                <v-list-item-title> {{ item.code }} </v-list-item-title>
                                                                            </v-list-item-content>
                                                                        </v-list-item>
                                                                    </v-list>
                                                                </v-menu>
                                                            </v-col>
                                                            <v-col>
                                                                <v-autocomplete
                                                                    v-model="taskNumber"
                                                                    label="Numer zadania"
                                                                    :items="tasksFound"
                                                                    :search-input.sync="taskSearchQuery"
                                                                    :filter="customTaskFilter"
                                                                    :loading="actionLoading.taskSearch"
                                                                    :disabled="!canReportOnWorkspace"
                                                                    hide-details
                                                                    item-value="primaryNumber"
                                                                    no-data-text="Nie znaleziono"
                                                                    clearable
                                                                    dense
                                                                    outlined
                                                                    @click:clear="clearTaskSearch"
                                                                    @change="getTaskFromSearchedData"
                                                                >
                                                                    <template slot="selection" slot-scope="data">
                                                                        {{ data.item.primaryNumber }}
                                                                    </template>

                                                                    <template slot="item" slot-scope="data">
                                                                        {{ data.item.primaryNumber }}
                                                                    </template>

                                                                    <template slot="no-data">
                                                                        <v-list-item>
                                                                            <v-list-item-title>
                                                                                <template v-if="!taskSearchQuery">
                                                                                    Wpisz treść aby odnaleźć <strong>zlecenie</strong>.
                                                                                </template>
                                                                                <template v-else-if="taskSearchQuery.length < taskSearchQueryMinLength">
                                                                                    Wpisz przynajmniej <strong>{{ taskSearchQueryMinLength }}</strong> znaki.
                                                                                </template>
                                                                                <template v-else-if="actionLoading.taskSearch">
                                                                                    Trwa wyszukiwanie zleceń...
                                                                                </template>
                                                                                <template v-else>
                                                                                    Nie odnaleziono zleceń na zapytanie: "<strong> {{ taskSearchQuery }}".</strong>
                                                                                </template>
                                                                            </v-list-item-title>
                                                                        </v-list-item>
                                                                    </template>
                                                                </v-autocomplete>
                                                            </v-col>
                                                            <v-col cols="auto">
                                                                <v-btn
                                                                    v-if="hasCamera"
                                                                    fab
                                                                    x-small
                                                                    color="primary"
                                                                    class="hidden-md-and-up elevation-1 mx-3"
                                                                    data-sentry-label="Otwórz czytnik kodów"
                                                                    @click="showCodeReaderDialog({
                                                                        expectedQrTypeCodes: ['KZ', 'KP'],
                                                                        origin: null
                                                                    })"
                                                                >
                                                                    <v-icon>mdi-camera</v-icon>
                                                                </v-btn>
                                                            </v-col>
                                                        </v-row>
                                                    </v-container>
                                                </v-col>
                                            </v-row>
                                        </v-container>

                                        <v-divider />
                                        <v-container v-if="currentWorkspace.typeId == 4" fluid class="pa-0">
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-data-table
                                                        :items="reportItemOrders"
                                                        :loading="actionLoading.reportItemOrders"
                                                        :headers="reportItemOrdersHeaders"
                                                        no-data-text="Brak Zamówień"
                                                        :items-per-page="5"
                                                        :page.sync="currentPaginationPage"
                                                        :item-class="scheduledTaskClass"
                                                        :footer-props="{
                                                            itemsPerPageText: 'Zamówień na stronie',
                                                            showFirstLastPage: false,
                                                        }"
                                                        :mobile-breakpoint="0"
                                                    >
                                                        <template #item.priority="{item}">
                                                            <v-tooltip v-if="item.priority" :open-delay="tooltipDelay" top>
                                                                <template #activator="{on}">
                                                                    <span
                                                                        icon
                                                                        v-on="on"
                                                                    >
                                                                        <v-icon color="pink">
                                                                            mdi-priority-high
                                                                        </v-icon>
                                                                    </span>
                                                                </template>
                                                                <span>Priorytet</span>
                                                            </v-tooltip>
                                                            <v-tooltip v-else :open-delay="tooltipDelay" top>
                                                                <template #activator="{on}">
                                                                    <span
                                                                        icon
                                                                        v-on="on"
                                                                    >
                                                                        <v-icon color="grey lighten-2">
                                                                            mdi-priority-high
                                                                        </v-icon>
                                                                    </span>
                                                                </template>
                                                                <span>Priorytet</span>
                                                            </v-tooltip>
                                                        </template>
                                                        <template #item.delayed="{item}">
                                                            <v-tooltip v-if="item.delayed" :open-delay="tooltipDelay" top>
                                                                <template #activator="{on}">
                                                                    <span
                                                                        icon
                                                                        v-on="on"
                                                                    >
                                                                        <v-icon color="warning">
                                                                            mdi-clock-outline
                                                                        </v-icon>
                                                                    </span>
                                                                </template>
                                                                <span>Opóźnione</span>
                                                            </v-tooltip>
                                                            <v-tooltip v-else :open-delay="tooltipDelay" top>
                                                                <template #activator="{on}">
                                                                    <span
                                                                        icon
                                                                        v-on="on"
                                                                    >
                                                                        <v-icon color="grey lighten-3">
                                                                            mdi-clock-outline
                                                                        </v-icon>
                                                                    </span>
                                                                </template>
                                                                <span>Opóźnione</span>
                                                            </v-tooltip>
                                                        </template>
                                                        <template #item.primaryNumber="{item}">
                                                            {{ item.primaryNumber }}
                                                        </template>
                                                        <template #item.amount="{item}">
                                                            {{ item.amount }}
                                                        </template>
                                                        <template #item.productName="{item}">
                                                            {{ item.productName }}
                                                        </template>
                                                        <template #item.plannedSendDate="{item}">
                                                            {{ formatDate(item.plannedSendDate, withTime = false) }}
                                                        </template>
                                                        <template #item.shippingGroup="{item}">
                                                            {{ item.shippingGroup }}
                                                        </template>
                                                        <template #item.isComplete="{item}">
                                                            <v-icon
                                                                v-if="item.isComplete"
                                                                small
                                                                color="success"
                                                            >
                                                                mdi-check
                                                            </v-icon>
                                                            <v-icon v-else small color="error">
                                                                mdi-close
                                                            </v-icon>
                                                        </template>
                                                        <template #item.actions="{item}">
                                                            <v-btn
                                                                color="primary"
                                                                text
                                                                data-sentry-label="Wybierz"
                                                                @click="getTaskFromOrdersData(item.primaryNumber)"
                                                            >
                                                                Wybierz
                                                            </v-btn>
                                                        </template>
                                                    </v-data-table>
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                        <v-container v-else fluid class="pa-0">
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-data-table
                                                        :items="scheduledTasks"
                                                        :loading="actionLoading.scheduledTasks"
                                                        :headers="actualHeaders"
                                                        no-data-text="Brak zadań"
                                                        :items-per-page="5"
                                                        :page.sync="currentPaginationPage"
                                                        :item-class="scheduledTaskClass"
                                                        :footer-props="{
                                                            itemsPerPageText: 'Zadań na stronie',
                                                            showFirstLastPage: false,
                                                        }"
                                                        :mobile-breakpoint="0"
                                                    >
                                                        <template #item.taskNumber="{item}">
                                                            {{ item.procNumber }}
                                                            <v-tooltip
                                                                v-if="item.objectType === 2"
                                                                :open-delay="tooltipDelay"
                                                                top
                                                            >
                                                                <template #activator="{on}">
                                                                    <v-chip
                                                                        class="pa-1 ml-1"
                                                                        color="transparent"
                                                                        v-on="on"
                                                                        @click="showReportOrderJobsListDialog(item.procNumber)"
                                                                    >
                                                                        <v-icon color="green">
                                                                            mdi-comment-text-outline
                                                                        </v-icon>
                                                                    </v-chip>
                                                                </template>
                                                                <span>Podgląd numerów zleceń</span>
                                                            </v-tooltip>
                                                        </template>
                                                        <template #item.plate="{item}">
                                                            {{ parseEmptyValue(item.procPlateNumber) }}
                                                        </template>
                                                        <template #item.closedFormat="{item}">
                                                            {{ parseEmptyValue(item.orderObject && item.orderObject.closedFormat) }}
                                                        </template>
                                                        <template #item.quantity="{item}">
                                                            {{ item.procTaskQuantity }}
                                                        </template>
                                                        <template #item.volume="{item}">
                                                            {{ parseEmptyValue(item.orderObject && item.orderObject.volume) }}
                                                        </template>
                                                        <template #item.foldedSignaturesQuantity="{item}">
                                                            {{ parseEmptyValue(item.orderObject && item.orderObject.foldedSignaturesQuantity) }}
                                                        </template>
                                                        <template #item.coverStatus="{item}">
                                                            {{ parseEmptyValue(item.orderObject && item.orderObject.coverStatus) }}
                                                        </template>
                                                        <template #item.innerStatus="{item}">
                                                            {{ parseEmptyValue(item.orderObject && item.orderObject.innerStatus) }}
                                                        </template>
                                                        <template #item.date="{item}">
                                                            {{ formatDate(item.procTaskStartTime) }}
                                                        </template>
                                                        <template #item.procTaskStartTime="{item}">
                                                            {{ formatDate(item.procTaskStartTime) }}
                                                        </template>
                                                        <template #item.procTaskDuration="{item}">
                                                            {{ parseDuration(item.procTaskDurationSeconds) }}
                                                        </template>
                                                        <template #item.status="{item}">
                                                            {{ item.procStatus | lowercase }}
                                                        </template>
                                                        <template #item.materialStatus="{item}">
                                                            <div class="d-inline-flex">
                                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                                    <template #activator="{on}">
                                                                        <v-icon v-if="isWarehouseHasReportedStatus(item)" color="green" v-on="on">
                                                                            mdi-clipboard-check
                                                                        </v-icon>
                                                                        <v-icon v-else color="pink" v-on="on">
                                                                            mdi-clipboard-alert
                                                                        </v-icon>
                                                                    </template>
                                                                    <div>
                                                                        <div>MAGAZYN: {{ parseEmptyValue(item.wareHouseStatus) }}</div>
                                                                        <div>CTP: {{ parseEmptyValue(item.STPStatus) }}</div>
                                                                        <div>GUMA: {{ parseEmptyValue(item.rubberStatus) }}</div>
                                                                    </div>
                                                                </v-tooltip>
                                                            </div>
                                                        </template>
                                                        <template #item.productionProcessFB="{item}">
                                                            {{ parseEmptyValue(item.productionProcessFB && item.productionProcessFB.name) }}
                                                        </template>
                                                        <template #item.actions="{item}">
                                                            <v-btn
                                                                color="primary"
                                                                text
                                                                data-sentry-label="Wybierz"
                                                                @click="getTaskFromScheduledTaskData(item)"
                                                            >
                                                                Wybierz
                                                            </v-btn>
                                                        </template>
                                                    </v-data-table>
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-fade-transition>

                        <v-fade-transition>
                            <v-row v-if="isReportingSessionActive" dense justify="space-between">
                                <v-col cols="auto">
                                    <p><strong>Zaraportowany czas:</strong> {{ parseDuration(totalDuration) }}</p>
                                </v-col>
                                <v-col cols="auto">
                                    <v-btn
                                        color="primary"
                                        text
                                        :loading="actionLoading.detailedReportsList"
                                        data-sentry-label="Pokaż moje raporty"
                                        @click="showDetailedReportsListDialog"
                                    >
                                        Pokaż moje raporty
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-fade-transition>
                    </template>
                    <template v-if="canReportOnWorkspace && !isWorkspaceConfigured">
                        <v-row>
                            <v-container>
                                <v-row justify="center">
                                    <v-col cols="12">
                                        <v-alert
                                            type="warning"
                                            text
                                            icon="mdi-alert"
                                            border="left"
                                        >
                                            Konfiguracja na tym stanowisku jest niepełna, zgłoś się do administratora.
                                        </v-alert>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-row>
                    </template>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import store from '@/store'

import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import CreateProcessReportDialog from './CreateProcessReportDialog'
import CreateAccidentDialog from '@/views/Maintenance/Accidents/CreateAccidentDialog'
import DetailedReportsListDialog from './DetailedReportsListDialog'
import CodeReaderDialog from '@/components/common/CodeReaderDialog'
import ReportOutOfSequenceCodeDialog from './ReportOutOfSequenceCodeDialog'
import ProcessDialog from './ProcessDialog'
import WorkspaceAccidentsListDialog from './WorkspaceAccidentsListDialog'
import WorkspaceNotesListDialog from './WorkspaceNotesListDialog'
import ReportOrderJobsListDialog from './ReportOrderJobsListDialog'
import ReportShiftSummaryDialog from '@/components/common/ReportShiftSummaryDialog'
import ReportingProcessingReportsTable from '../Common/ReportingProcessingReportsTable'
import WorkspaceSelector from '../Common/WorkspaceSelector'

import filters from '@/mixins/dataTableFilters'
import dateFormatter from '@/mixins/dateFormatter'
import decodeQRcode from '@/mixins/decodeQRcode'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse.js'
import pagination from '@/mixins/dataTablePagination'
import { appConfig } from '@/config'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'
import { groupBy } from 'lodash'

const CUT_OUT_ELASTIC_PROCESS_ID = 130
let taskSearchTimer

export default {
    name: 'Reporting',
    components: {
        PageTitle,
        ConfirmDialog,
        ReportShiftSummaryDialog,
        CreateProcessReportDialog,
        CreateAccidentDialog,
        DetailedReportsListDialog,
        CodeReaderDialog,
        ReportOutOfSequenceCodeDialog,
        ReportOrderJobsListDialog,
        ProcessDialog,
        WorkspaceAccidentsListDialog,
        WorkspaceNotesListDialog,
        ReportingProcessingReportsTable,
        WorkspaceSelector,
    },
    filters: {
        lowercase(value) {
            value = value.toString()

            return value.toLowerCase()
        }
    },
    mixins: [ dateFormatter, decodeQRcode, tooltipDelay, parse, pagination, filters ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('configurations/getProductionProcesses'),
            store.dispatch('configurations/getProductionSites', {query:'?type_id=2,3,4,5,7'}),
            store.dispatch('reports/getUnfinishedReportsShiftSummary', store.state.user.userId),
            store.dispatch('reports/getShippingGroups')
        ])
            .then(responses => {
                const workspace = JSON.parse(localStorage.getItem('workspace'))
                const unfinishedReportsShiftSummary = responses[2].data.results

                if (unfinishedReportsShiftSummary.length > 0) {
                    store.dispatch('reports/getDetailedUserReports', {
                        reportShiftSummaryId: `?reportShiftSummary_id=${unfinishedReportsShiftSummary[0].id}`,
                        query: '&statusId=1'
                    })
                        .then(response => {
                            const unfinishedDetailedReport = response.data.results

                            if (unfinishedDetailedReport.length > 0 && unfinishedDetailedReport[0].productionSiteId === workspace?.workspaceId) {
                                next({
                                    name: 'reportsReportingReport',
                                    params: {
                                        id: unfinishedDetailedReport[0].id
                                    }

                                })

                                store.dispatch('toggleMainLoader', false)
                            } else {
                                next(vm => {
                                    if (!workspace) {
                                        vm.showWorkspaceSettingsDialog()
                                    } else {
                                        vm.workspaceId = workspace.workspaceId
                                        vm.setReportSettings(vm.workspaceId)
                                    }

                                    vm.unfinishedReportsShiftSummary = unfinishedReportsShiftSummary[0]

                                    if (unfinishedDetailedReport.length > 0 && unfinishedDetailedReport[0].productionSiteId !== workspace?.workspaceId ) {
                                        vm.isReportUnfinished = true
                                        vm.unfinishedReport = unfinishedDetailedReport[0]
                                    }
                                })
                            }
                        })
                        .catch(error => {
                            next(error)
                            errorHandler(error)
                        })
                } else {
                    next(vm => {
                        if (!workspace) {
                            vm.showWorkspaceSettingsDialog()
                        } else {
                            vm.workspaceId = workspace.workspaceId
                            vm.setReportSettings(vm.workspaceId)
                        }
                    })
                }

            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('reports/clearReportItemOrdersFilters')
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            settings : {
                workspace: null,
                machine: null,
                process: null
            },
            workspaceId: null,
            workspaceSettingsDialog: false,
            lastReportsShiftSummary: [],
            unfinishedReportsShiftSummary: null,
            scheduledTasks: [],
            reportOrderJobs: [],
            procNumber: null,
            taskNumber: null,
            taskSearchQuery: null,
            taskSearchQueryMinLength: 3,
            tasksFound: [],
            taskTypeId: null,
            fbReportItemTypeId: null,
            currentPlate: null,
            createProcessReportDialog: false,
            reportProcesses: [],
            reportSettingsDialog: false,
            createAccidentDialog: false,
            accident: {
                id: null,
                machineId: null,
                accidentTypeId: null,
                description: null,
                accidentDepartmentId: null,
                username: null
            },
            detailedReportsListDialog: false,
            detailedReportsList: [],
            stoppedReportingSession: false,
            confirmStopReportingSessionDialog: false,
            currentPaginationPage: null,
            codeReaderSettings: {
                expectedQrTypeCodes: null,
                origin: null
            },
            codeReaderDialog: false,
            actionLoading: {
                reportingSession: false,
                scheduledTasks: false,
                taskSearch: false,
                createProcessReport: false,
                createAccident: false,
                detailedReportsList: false,
                workspaces: false,
                reportItemOrders: false,
                workspaceAccidentsList: false,
                approveReport: false,
                workspaceNotesList: false,
                reportOrderJobsList: false,
            },
            reportOutOfSequenceCodeDialog: false,
            outOfSequenceCodeId: null,
            currentTask: null,
            interruptedDetailedUserReports: [],
            interruptedDetailedUserReportsLoading: false,
            isTaskFromList: false,
            unfinishedReport: null,
            isReportUnfinished: false,
            processDialog: false,
            workspaceAccidentsListDialog: false,
            workspaceNotesListDialog: false,
            reportOrderJobsListDialog: false,
            unsolvedAccidents: [],
            workspaceNotes: [],
            unreadWorkspaceNotesAmount: '0',
            accidentStatusHistory: [],
            typeId: '',
            isScannerTask: false,
            reportItemOrders: [],
            reportShiftSummaryDialog: false,
            currentReportShiftSummary: null,
            headers: [
                {
                    text: 'Nr zamówienia',
                    value: 'taskNumber',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Montaż',
                    value: 'plate',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Format',
                    value: 'closedFormat',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Nakład',
                    value: 'quantity',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Objętość',
                    value: 'volume',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Ilość składek',
                    value: 'foldedSignaturesQuantity',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Status okładki',
                    value: 'coverStatus',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Status wnętrz',
                    value: 'innerStatus',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Zaplanowany start',
                    value: 'date',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Zaplanowany czas',
                    value: 'procTaskDuration',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Status',
                    value: 'status',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Proces',
                    value: 'productionProcessFB',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ],
            printHeaders: [
                {
                    text: 'Nr zlecenia',
                    value: 'procNumber',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Montaż',
                    value: 'procPlateNumber',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Sposób druku',
                    value: 'printMethod',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Rodzaj surowca',
                    value: 'paper',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Format surowca',
                    value: 'format',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Nakład brutto',
                    value: 'procTaskQuantity',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Zaplanowany start',
                    value: 'procTaskStartTime',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Czas druku',
                    value: 'procTaskDuration',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Kolorystyka str. A',
                    value: 'coloursPageA',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Kolorystyka str. B',
                    value: 'coloursPageB',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Lakier str. A',
                    value: 'varnishSideA',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Lakier str. B',
                    value: 'varnishSideB',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Liczarka (co ile arkuszy)',
                    value: 'inserterBatch',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Status materiałów',
                    value: 'materialStatus',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                }
            ],
            reportHeaders: [
                {
                    text: 'Numer',
                    value: 'objectNumber',
                    sortable: false
                },
                {
                    text: 'Maszyna',
                    value: 'machineSymbol',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Proces',
                    value: 'processName',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Montaż',
                    value: 'plateNumber',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Nakład wykonany',
                    value: 'reportAmount',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Ilość osób',
                    value: 'machineStuffAmount',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Status',
                    value: 'statusId',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data rozpoczęcia',
                    value: 'start',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ],
            reportItemOrdersHeaders: [
                {
                    text: 'Priorytet',
                    value: 'priority',
                    sortable: true,
                    width: 100,
                    align: 'center'
                },
                {
                    text: 'Opóźnienie',
                    value: 'delayed',
                    sortable: true,
                    width: 100,
                    align: 'center'
                },
                {
                    text: 'Numer zamówienia',
                    value: 'primaryNumber',
                    sortable: true,
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    sortable: true,
                    align: 'center',
                },
                {
                    text: 'Produkt',
                    value: 'productName',
                    sortable: true,
                    align: 'center',
                },
                {
                    text: 'Data wysyłki',
                    value: 'plannedSendDate',
                    sortable: true,
                    align: 'center',
                },
                {
                    text: 'Sortownia',
                    value: 'shippingGroup',
                    sortable: true,
                    align: 'center',
                },
                {
                    text: 'Komplet',
                    value: 'isComplete',
                    sortable: true,
                    align: 'center',
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                }
            ]
        }
    },
    computed: {
        userId() {
            return this.$store.state.user.userId
        },
        productionProcesses() {
            return this.$store.state.configurations.productProductionProcesses.productionProcesses
        },
        userMachines() {
            return this.$store.state.user.user.profile.machineRead
        },
        hasReportSettings() {
            return this.$store.state.reports.reporting.hasReportSettings
        },
        savedFilters() {
            return this.$store.state.reports.reporting.reportItemOrdersFilters
        },
        machineProcesses() {
            return this.$store.state.reports.reporting.machineProcesses
        },
        filteredMachineProcesses() {
            const typeId = this.tasksFound.find(item => item.primaryNumber == this.taskNumber)?.typeId
            let allowedProcesses = []

            if (typeId && this.taskNumber) {
                allowedProcesses = this.currentWorkspace.allowedReportItemTypes
                    .filter(item => item.reportItemType.id == typeId)
                    .map(item => item.productionProcess).flat()
            } else if (this.fbReportItemTypeId) {
                allowedProcesses = this.currentWorkspace.allowedReportItemTypes
                    .filter(item => item.reportItemType.id == this.fbReportItemTypeId)
                    .map(item => item.productionProcess).flat()
            } else if (this.taskTypeId) {
                if (this.taskTypeId === 1) {
                    allowedProcesses = this.reportSettings.workspace.allowedReportItemTypes.filter(item => {
                        return item.reportItemType.id === 10 || item.reportItemType.id === 20 || item.reportItemType.id === 30
                    }).map(item => item.productionProcess).flat()

                }
                if (this.taskTypeId === 2) {
                    allowedProcesses = this.reportSettings.workspace.allowedReportItemTypes.filter(item => {
                        return item.reportItemType.id === 15 || item.reportItemType.id === 40 || item.reportItemType.id === 50 || item.reportItemType.id === 60
                    }).map(item => item.productionProcess).flat()
                }
            } else {
                return this.machineProcesses.length > 1 && this.currentWorkspace?.defaultProductionProcess ?
                    this.machineProcesses.filter(item => item.id === this.currentWorkspace.defaultProductionProcess.id) : []
            }

            return allowedProcesses ?? []
        },
        defaultProcess() {
            if (this.currentWorkspace) {
                if (this.machineProcesses.length === 1) {
                    return this.machineProcesses[0]
                } else if (this.currentWorkspace.defaultProductionProcess) {
                    return this.currentWorkspace.defaultProductionProcess
                }
            }

            return {}
        },
        reportSettings() {
            return this.$store.state.reports.reporting.reportSettings
        },
        currentMachine() {
            return this.reportSettings.machine
        },
        currentWorkspace() {
            return this.reportSettings.workspace
        },
        userName() {
            return this.$store.state.user.userName
        },
        userShift() {
            return this.currentWorkspace ? this.currentWorkspace.userShift : 'brak danych'
        },
        hasCamera() {
            return this.$store.state.common.hasCamera
        },
        workspaces() {
            return this.$store.state.configurations.productionSites.productionSites
        },
        canReportOnWorkspace() {
            return this.workspaces.find(item => item.id === this.workspaceId)
        },
        isWorkspaceConfigured() {
            return this.currentWorkspace?.allowedReportItemTypes.length > 0
        },
        canChangeWorkspace() {
            return hasUserPermission('production_sites_admin_update')
        },
        isReportingSessionActive() {
            return this.unfinishedReportsShiftSummary !== null
        },
        totalDuration() {
            return this.$store.getters['reports/getDetailedUserReportsTotalDuration']
        },
        isPrintProcess() {
            return this.settings?.workspace?.typeId === 7 // print type
        },
        actualHeaders() {
            return !this.isPrintProcess ? this.headers : this.printHeaders
        },
        footerProps() {
            return {
                itemsPerPageOptions: appConfig.reports.rowsPerPageItems
            }
        },
        unsolvedAccidentsAmount() {
            return this.unsolvedAccidents.length.toString()
        },
        shippingGroups() {
            return this.$store.state.reports.reporting.shippingGroups
        },
        codes() {
            return this.shippingGroups.map(item =>
                [10, 20, 30, 40].includes(item.id) ? {...item, checked: true} : {...item, checked: false} )
        },
        filterQuery() {
            const filters = this.filters
            const connector = Object.keys(filters).length === 1 ? '?' : '&'
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat(`${connector}`, `${item}=${filters[item]}`)
                }
            }

            return query
        },
        currentDate() {
            return this.formatDate(new Date(), true, true, { weekday: 'long', year: 'numeric', month: 'numeric', day: 'numeric' })
        }
    },
    watch: {
        currentMachine(value) {
            this.getScheduledTasks(value.code)
                .then(() => {
                    this.resetPagination()
                })
                .catch(error => {
                    errorHandler(error)
                })
            if (this.isReportingSessionActive) this.getUserReports()
        },
        currentWorkspace(value) {
            if (value.typeId === 4) {
                this.$store.dispatch('reports/clearReportItemOrdersFilters').then(() => {
                    this.filters = Object.assign({}, this.savedFilters)
                    this.getReportItemOrders()
                })
            }
        },
        taskSearchQuery(value = null) {
            if (value === null || value.length < this.taskSearchQueryMinLength) return

            this.actionLoading.taskSearch = true

            clearTimeout(taskSearchTimer)

            taskSearchTimer = setTimeout(() => {
                if (value) {
                    this.searchTasks(value)
                        .catch(error => {
                            errorHandler(error)
                        })
                } else {
                    this.actionLoading.taskSearch = false
                    this.tasksFound = []
                }
            }, 500)
        }
    },
    created() {
        document.addEventListener('keydown', this.QRtoJSON)
        this.pagination.itemsPerPage = 5
    },
    destroyed() {
        document.removeEventListener('keydown', this.QRtoJSON)
    },
    methods: {
        getReportItemOrders() {
            this.actionLoading.reportItemOrders = true
            store.dispatch('reports/getReportItemOrders', {
                productionSiteId: this.workspaceId,
                query: this.filterQuery
            })
                .then(response => {
                    this.reportItemOrders = response.results
                    this.resetPagination()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.reportItemOrders = false
                })
        },
        scheduledTaskClass(item) {
            if (item.procStatus === 'WSTRZYMANA') return 'v-data-table__label v-data-table__label--warning'
        },
        openReport(reportId) {
            this.savePagination(this.$options.name)
            router.push({
                name: 'reportsReportingReport',
                params: {
                    id: reportId,
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        isWarehouseHasReportedStatus(item) {
            return item.wareHouseStatus !== 'NOT_REPORTED' && item.rubberStatus !== false && item.CTPStatus !== false
        },
        async getWorkspace(workspaceId) {
            return await this.$store.dispatch('configurations/getProductionSite', workspaceId)
                .catch(error => {
                    errorHandler(error)
                })
        },
        setReportSettings(workspaceId, resetProcess = false) {
            this.getWorkspace(workspaceId).then(response => {
                this.workspaceId = response.data.id
                localStorage.setItem('workspace', JSON.stringify({
                    'workspaceId': workspaceId, 
                    'allowsSendingOrders': response.data.allowsSendingOrders,
                    'allowsReportExcessAmount': response.data.reportOverMaxAmount,
                    'isPalletNumberRequired': response.data.isPalletNumberRequired,
                    'reportElastic': response.data.reportElastic,
                    'workspaceTypeId': response.data.typeId,
                    'machineCode': response.data.machine.code
                }))
                this.settings.workspace = response.data
                this.settings.machine = response.data.machine

                const machineProcessesIds = response.data.machine.productionProcess
                const machineProcesses = this.productionProcesses.filter(item => machineProcessesIds.includes(item.id))

                this.settings.process = resetProcess ? this.settings.process = null : this.reportSettings.process

                store.dispatch('reports/setReportSettings', this.settings)
                store.dispatch('reports/setMachineProcesses', machineProcesses)

                this.getWorkspaceAccidentsList()
                this.getWorkspaceUnreadNotesAmount()

                if (this.unfinishedReport && this.unfinishedReport.productionSiteId === workspaceId) {
                    this.$router.push({
                        name: 'reportsReportingReport',
                        params: {
                            id: this.unfinishedReport.id
                        }
                    })
                        .catch(error => errorHandler(error))

                    return
                }

                this.getScheduledTasks(this.currentMachine.code)
                    .then(() => {
                        this.resetPagination()
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
            })
        },
        updateReportSettings() {
            this.$store.dispatch('reports/setReportSettings', this.reportSettings)
        },
        showWorkspaceSettingsDialog() {
            this.actionLoading.workspaces = true
            addSentryBreadcrumb('ui.action', 'Ustawienia stanowiska')

            this.$store.dispatch('configurations/getProductionSites', {query:'?type_id=2,3,4,5,7'})
                .then(() => {
                    this.workspaceSettingsDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.workspaces = false
                    this.actionLoading.reportingSession = false
                })
        },
        saveWorkspaceSettings(value) {
            this.setReportSettings(value, true)
        },
        startReportingSession() {
            if (!this.workspaceId) {
                if (!this.canChangeWorkspace) this.actionLoading.reportingSession = true
                this.showWorkspaceSettingsDialog()
            }
            else if (!this.canReportOnWorkspace && !this.canChangeWorkspace) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie można rozpocząć raportowania na wybranym stanowisku.'
                })
            } else {
                this.actionLoading.reportingSession = true

                this.$store.dispatch('reports/startReportingSession')
                    .then(response => {
                        this.unfinishedReportsShiftSummary = response.data
                        this.interruptedDetailedUserReports = []

                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Rozpoczęto sesję.'
                        })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading.reportingSession = false
                    })
            }
        },
        showConfirmStopReportingSessionDialog() {
            this.confirmStopReportingSessionDialog = true
        },
        closeConfirmStopReportingSessionDialog() {
            this.confirmStopReportingSessionDialog = false
        },
        stopReportingSession() {
            this.actionLoading.reportingSession = true

            this.$store.dispatch('reports/stopReportingSession', {
                id: this.unfinishedReportsShiftSummary.id,
                statusId: 10
            })
                .then(() => {
                    this.showReportShiftSummaryDialog()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmStopReportingSessionDialog()
                    this.actionLoading.reportingSession = false
                })
        },
        showReportShiftSummaryDialog() {
            this.stoppedReportingSession = true
            this.$store.dispatch('toggleMainLoader', true)

            this.$store.dispatch('reports/getDetailedReports', {
                reportShiftSummary_id: this.unfinishedReportsShiftSummary.id
            })
                .then(response => {
                    this.currentReportShiftSummary = {
                        ...this.unfinishedReportsShiftSummary,
                        detailedReports: response.data.results
                    }

                    this.reportShiftSummaryDialog = true
                    this.unfinishedReportsShiftSummary = null
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$store.dispatch('toggleMainLoader', false)
                })
        },
        closeReportShiftSummaryDialog() {
            this.currentReportShiftSummary = null
            this.reportShiftSummaryDialog = false
            setTimeout(() => {
                this.stoppedReportingSession = false
            }, 500)
        },
        async getScheduledTasks(machineCode) {
            this.actionLoading.scheduledTasks = true

            return await store.dispatch('reports/getScheduledTasks', { machineCode })
                .then(response => {
                    this.scheduledTasks = response.data.results
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.actionLoading.scheduledTasks = false
                })
        },
        async searchTasks(taskNumber = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie zadania')

            const typesList = this.reportSettings.workspace.allowedReportItemTypes.map(item => {
                return item.reportItemType.id
            })

            this.typeId = typesList.join() //zlecenia z zamówień, zlecenia fb

            const payload = {
                taskNumber: taskNumber,
                typeId: this.typeId,
            }

            if (this.settings.workspace.reportElastic) {
                payload['elastic'] = true
            }

            return await this.$store.dispatch('reports/findTask', payload)
                .then(response => {
                    this.tasksFound = response.data.results

                    return response.data.results
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.actionLoading.taskSearch = false
                    this.isTaskFromList = false
                })
        },
        async getTask(taskNumber, taskId = null, plateNumber = null) {
            if (this.tasksFound.length === 0) return

            const id = taskId || this.tasksFound.find(item => item.primaryNumber == taskNumber)?.id

            this.$store.dispatch('toggleCircularLoader', true)

            return await this.$store.dispatch('reports/getTask', {
                taskId: id,
                processId: this.reportSettings.process.id
            })
                .then(task => {
                    if (!this.currentPlate) {
                        if (plateNumber) {
                            this.currentPlate = task.reportItemPlates.find(item => item.plateNumber == plateNumber)
                        } else {
                            if (this.reportSettings.process.id === CUT_OUT_ELASTIC_PROCESS_ID) {
                                this.currentPlate = task.reportItemPlates.find(item => !item.isFullReported && item.hasNewElastic)
                            } else {
                                this.currentPlate = task.reportItemPlates.find(item => !item.isFullReported)
                            }
                        }
                    }

                    if (this.currentPlate) {
                        this.isPrintProcess
                            ? this.redirectToCreatePrintReport(task)
                            : this.redirectToCreateReport(task)
                    } else {
                        this.$store.dispatch('showNotification', {
                            type: 'info',
                            message: 'Wybrane zadanie jest już w pełni zaraportowane.'
                        })
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$store.dispatch('toggleCircularLoader', false)
                })
        },
        clearTaskSearch() {
            this.taskSearchQuery = null
            this.tasksFound = []
        },
        isSelectedTaskOutOfOrder(task) {
            const index = this.scheduledTasks.indexOf(task)
            const hasNewTasksBeforeSelected = this.scheduledTasks.slice(0, index).some(item => item.procStatus === 'UTWORZONA')

            if (index !== 0 && hasNewTasksBeforeSelected) {
                this.showReportOutOfSequenceCodeDialog(task)
                this.currentTask = task
            } else {
                this.findTask(task)
            }
        },
        getTaskFromScheduledTaskData(task) {
            if (!task.productionProcessFB) {
                this.currentTask = task
                this.showProcessDialog()
                errorHandler('Dodany proces dla zadania z planu nie istnieje w bazie.', 'info')
            } else {
                this.setProcess(task)
                this.isSelectedTaskOutOfOrder(task)
            }
        },
        getTaskFromSearchedData(taskNumber, task, isScannerTask = false) {
            if (!taskNumber && !task) return

            this.resetProcess()
            this.isScannerTask = isScannerTask

            this.taskNumber = taskNumber
            this.showProcessDialog()
        },
        getTaskFromOrdersData(taskNumber, task, isScannerTask = false) {
            if (!taskNumber && !task) return

            this.resetProcess()
            this.isScannerTask = isScannerTask

            this.taskNumber = taskNumber
            this.searchTasks()
            this.showProcessDialog()
        },
        findTask(task) {
            const taskNumber = task.procNumber || (task.taskTypeId === 2 ? task.order : task.taskNumber), // zamówienie
                plateNumber = isNaN(task.procPlateNumber) ? 1 : task.procPlateNumber // Jeśli zadanie z planu nie zawiera informacji o numerze montażu, ustawiamy domyślnie pierwszy

            this.$store.dispatch('toggleCircularLoader', true)
            this.searchTasks(taskNumber)
                .then(response => {
                    if (response.length > 0) {
                        const taskFound = response[0]

                        this.getTask(taskNumber, taskFound.id, plateNumber)
                            .finally(() => {
                                this.$store.dispatch('toggleCircularLoader', false)
                            })
                    } else {
                        this.$store.dispatch('toggleCircularLoader', false)
                        this.$store.dispatch('showNotification', {
                            type: 'error',
                            message: 'Wybrane zadanie nie jest możliwe do zaraportowania.'
                        })
                    }
                })
                .catch(error => {
                    this.$store.dispatch('toggleCircularLoader', false)
                    errorHandler(error)
                })
        },
        redirectToCreatePrintReport(task) {
            const currentPlate = groupBy(task.reportItemPlates, 'plateNumber')[this.currentPlate.plateNumber]
            const isABEmergency = !!currentPlate?.filter(
                item => item.printSideType === 'AB' && item.emergencyMode
            ).length
            const isABReported = !!currentPlate?.filter(
                item => item.printSideType === 'AB' && item.isFullReported
            ).length

            if (isABEmergency || isABReported) {
                const isSomeItemCanBeReported = !!currentPlate.filter(
                    item => item.printSideType !== 'AB' && !item.isFullReported
                ).length

                if (!isSomeItemCanBeReported || isABReported) {
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: 'Zlecenie zaraportowane w całości.',
                    })

                    return
                }
            }

            this.savePagination(this.$options.name)
            router.push({
                name: 'ReportsReportingCreatePrint',
                params: {
                    task: task,
                    plate: this.currentPlate,
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        redirectToCreateReport(task) {
            this.savePagination(this.$options.name)
            router.push({
                name: 'ReportsReportingCreate',
                params: {
                    task: task,
                    plate: this.currentPlate,
                    outOfSequenceCodeId: this.outOfSequenceCodeId,
                    lastParameter: this.settings.workspace.lastPalletEan
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showDetailedReportsListDialog() {
            this.actionLoading.detailedReportsList = true

            this.$store.dispatch('reports/getDetailedUserReports', {reportShiftSummaryId: `?reportShiftSummary_id=${this.unfinishedReportsShiftSummary.id}`})
                .then(response => {
                    this.detailedReportsList = response.data.results
                    this.detailedReportsListDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.detailedReportsList = false
                })
        },
        closeDetailedReportsListDialog() {
            this.detailedReportsListDialog = false
        },
        showCreateProcessReportDialog() {
            this.actionLoading.createProcessReport = true
            this.$store.dispatch('reports/getReportProcesses', this.workspaceId)
                .then(response => {
                    this.reportProcesses = response.data.results
                    this.createProcessReportDialog = true
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.createProcessReport = false
                })

            addSentryBreadcrumb('ui.action', 'Raportuj czynność')
        },
        showWorkspaceNotesListDialog() {
            this.actionLoading.workspaceNotesList = true

            this.$store.dispatch('notes/getProductionSiteNotes', {
                paginate: this.paginate(),
                query: `&productionSite_id=${this.workspaceId}`
            })
                .then(response => {
                    this.workspaceNotes = response.data.results
                    this.workspaceNotesListDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.workspaceNotesList = false
                })
            
            addSentryBreadcrumb('ui.action', 'Notki')
        },
        showReportOrderJobsListDialog(procNumber) {
            this.actionLoading.reportOrderJobsList = true

            this.$store.dispatch('reports/getReportItemOrderJobs', {
                query: `?orderNumber=${procNumber}`
            })
                .then(response => {
                    this.procNumber = procNumber
                    this.reportOrderJobs = response.results
                    this.reportOrderJobsListDialog = true
                })
                .catch(error => {
                    this.reportOrderJobsListDialog = true
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.reportOrderJobsList = false
                })
        },
        showWorkspaceAccidentsListDialog() {
            this.actionLoading.workspaceAccidentsList = true

            this.$store.dispatch('reports/getUnsolvedMachineAccidents', this.settings.machine.id)
                .then(response => {
                    this.unsolvedAccidents = response.data.results
                    this.workspaceAccidentsListDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.workspaceAccidentsList = false
                })
        },
        getWorkspaceAccidentsList() {
            this.$store.dispatch('reports/getUnsolvedMachineAccidents', this.settings.machine.id)
                .then(response => {
                    this.unsolvedAccidents = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getWorkspaceUnreadNotesAmount() {
            this.$store.dispatch('notes/getUnreadProductionSiteNotesAmount', this.currentWorkspace.id)
                .then(response => {
                    this.unreadWorkspaceNotesAmount = response.data.unreadProductionSiteNotes.toString()
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeWorkspaceAccidentsListDialog() {
            this.workspaceAccidentsListDialog = false
        },
        closeWorkspaceNotesListDialog() {
            this.getWorkspaceUnreadNotesAmount()
            this.workspaceNotesListDialog = false
        },
        closeReportOrderJobsListDialog() {
            this.reportOrderJobsListDialog = false
            this.procNumber = null
        },
        closeCreateProcessReportDialog() {
            this.createProcessReportDialog = false
        },
        showCreateAccidentDialog() {
            this.accident.machineId = this.currentMachine.id
            this.actionLoading.createAccident = true

            Promise.all([
                this.$store.dispatch('maintenance/getAccidentTypes'),
                this.$store.dispatch('maintenance/getMachines'),
                this.$store.dispatch('maintenance/getAccidentDepartments')
            ])
                .then(() => {
                    this.createAccidentDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.createAccident = false
                })

            addSentryBreadcrumb('ui.action', 'Zgłoszenie awarii')
        },
        closeCreateAccidentDialog() {
            this.createAccidentDialog = false

            setTimeout(() => {
                this.accident.id = null
                this.accident.accidentTypeId = null
                this.accident.machineId = null
                this.accident.description = null
                this.accident.username = null
                this.accident.accidentDepartmentId = null
            }, 500)
        },
        createAccident() {
            this.accident.username = this.userName
            this.$store.dispatch('maintenance/createAccident', this.accident)
                .then(response => {
                    if (response.data.accidentTypeId === 1) { // Stop maszyny
                        const accidentProcessId = 5

                        this.$store.dispatch('reports/createReport', {
                            objectType: 3, // Inne zadanie
                            objectId: accidentProcessId,
                            objectNumber: '-',
                            processCodeId: accidentProcessId,
                            machine: this.reportSettings.machine.id,
                            user: this.userId,
                            currentlyReportedQuantity: 0,
                            productionSiteId: this.workspaceId
                        })
                            .then(response => {
                                this.$router.push({
                                    name: 'reportsReportingReport',
                                    params: {
                                        id: response.id,
                                    }
                                })
                                    .catch(error => {
                                        errorHandler(error)
                                    })
                            })
                            .catch(error => errorHandler(error))
                            .finally(() => {
                                this.closeCreateAccidentDialog()
                            })
                    } else {
                        this.getWorkspaceAccidentsList()
                        this.closeCreateAccidentDialog()
                    }
                })
                .catch(error => {
                    this.closeCreateAccidentDialog()
                    errorHandler(error)
                })
        },
        customTaskFilter(item, queryText) {
            const task = item.primaryNumber.toLowerCase(),
                search = queryText.toLowerCase()

            return task.indexOf(search) > -1
        },
        updateShippingGroupFilter(code) {
            let selectedShippingGroups = this.filters.shippingGroupCodes != '' ? this.filters.shippingGroupCodes.split(',') : []

            const index = selectedShippingGroups.findIndex(item => item == code)

            if (index > -1) {
                selectedShippingGroups.splice(index, 1)
            } else {
                selectedShippingGroups.push(code)
            }

            this.filters.shippingGroupCodes = selectedShippingGroups.length > 0 ? selectedShippingGroups.join(',') : ''

            this.changeFilter()
        },
        changeFilter() {
            this.$store.dispatch('reports/setReportItemOrdersFilters', this.filters)
            this.resetPagination()
            this.getReportItemOrders()
        },
        resetPagination() {
            this.currentPaginationPage = 1
        },
        showCodeReaderDialog(data) {
            this.codeReaderSettings = data
            this.codeReaderDialog = true
        },
        hideCodeReaderDialog() {
            this.codeReaderDialog = false
        },
        handleQrJSON({ data }) {
            if (!this.isReportingSessionActive) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie rozpoczęto pracy.'
                })

                return
            }

            if (!this.canReportOnWorkspace) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie można raportować na wybranym stanowisku.'
                })

                return
            }

            if (!this.hasReportSettings) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie ustawiono stanowiska.'
                })

                return
            }

            if (data.type === 'KZ' || data.type === 'KP') {
                this.scannerEnterFallback(() => {
                    if (!this.canTaskBeReportedOnWorkspace(data)) return
                    this.taskNumber = data.taskNumber
                    this.taskTypeId = data.taskTypeId
                    this.fbReportItemTypeId = data.fbReportItemTypeId ?? null
                    this.getTaskFromSearchedData(null, data, true)
                    this.currentTask = data
                })
            }
            else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('reports.messages.unsupportedQrCode')
                })
            }
        },
        showReportOutOfSequenceCodeDialog(task) {
            this.currentTask = task
            this.$store.dispatch('reports/getReportOutOfSequenceCodes')
                .then(() => {
                    this.reportOutOfSequenceCodeDialog = true
                })
                .catch(error => errorHandler(error))
        },
        saveReportOutOfSequenceCode(outOfSequenceCodeId) {
            this.outOfSequenceCodeId = outOfSequenceCodeId
            this.findTask(this.currentTask)
        },
        closeReportOutOfSequenceCodeDialog() {
            this.reportOutOfSequenceCodeDialog = false
            this.currentTask = null
        },
        setProcess(task) {
            this.reportSettings.process = task.productionProcessFB
            this.updateReportSettings()
        },
        resetProcess() {
            this.reportSettings.process = null
            this.updateReportSettings()
        },
        canTaskBeReportedOnWorkspace(task) {
            let allowedTypesLength = 0

            if (task.taskTypeId === 1) {
                allowedTypesLength = this.reportSettings.workspace.allowedReportItemTypes.filter(item => {
                    return item.reportItemType.id === 10 || item.reportItemType.id === 20 || item.reportItemType.id === 30
                }).length

                if (allowedTypesLength === 0) {
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: 'Wybrane stanowisko nie obsługuje zleceń.'
                    })
                }
            }
            if (task.taskTypeId === 2) {
                allowedTypesLength = this.reportSettings.workspace.allowedReportItemTypes.filter(item => {
                    return item.reportItemType.id === 15 || item.reportItemType.id === 40 || item.reportItemType.id === 50
                }).length

                if (allowedTypesLength === 0) {
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: 'Wybrane stanowisko nie obsługuje zamówień.'
                    })
                }
            }

            return allowedTypesLength > 0
        },
        showProcessDialog() {
            this.processDialog = true
        },
        closeProcessDialog() {
            this.processDialog = false
            this.taskTypeId = null
            this.fbReportItemTypeId = null
        },
        saveProcess(data) {
            this.reportSettings.process = data
            this.updateReportSettings()

            if (this.currentTask) {
                if (this.isScannerTask) {
                    this.findTask(this.currentTask)
                } else {
                    this.isSelectedTaskOutOfOrder(this.currentTask)
                }
            } else {
                this.getTask(this.taskNumber)
            }
        },
        getUserReports() {
            this.interruptedDetailedUserReportsLoading = true
            store.dispatch('reports/getDetailedUserReports', {
                reportShiftSummaryId: `&reportShiftSummary_id=${this.unfinishedReportsShiftSummary.id}`,
                paginate: this.paginate(),
                query: `&statusId=90&machineId=${this.currentMachine.id}`
            })
                .then(response => {
                    this.interruptedDetailedUserReports = response.data.results
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.interruptedDetailedUserReportsLoading = false
                })
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getUserReports()
            }
            this.clearPaginationPending()
        }
    },
}
</script>